import React, { useEffect, useMemo, useState } from "react";
import { PrimaryButton } from "@/components/Buttons.jsx";
import { QRCode } from "react-qrcode-logo";
import { useSelector } from "react-redux";
import config from '@/config';
import Tooltip from "@/components/Tooltip.jsx";

const Marketing = () => {
  const { selectedMerchant, selectedLocation } = useSelector(
    (state) => state.app
  );
  const [includeLogo, setIncludeLogo] = useState(false);
  const [fullSizeLogo, setFullSizeLogo] = useState(false);
  const [qrStyle, setQrStyle] = useState("square");

  const [mothershipIncludeLogo, setMothershipIncludeLogo] = useState(false);
  const [mothershipFullSizeLogo, setMothershipFullSizeLogo] = useState(false);
  const [mothershipQrStyle, setMothershipQrStyle] = useState("square");

  const mothershipQrCodeOptions = useMemo(() => {
    if (!selectedMerchant) return null;
    const obj = {
      enableCORS: true,
      size: 230,
      value: `${config.direct_web_prefix}${
        selectedMerchant.direct_web_url ?? selectedMerchant.easy_id
      }`,
      qrStyle: mothershipQrStyle,
    };
    if (mothershipIncludeLogo) {
      if (mothershipFullSizeLogo) {
        obj.logoWidth = 230;
        obj.logoOpacity = 0.4;
      } else {
        obj.removeQrCodeBehindLogo = true;
        obj.logoWidth = 46;
      }
      obj.logoImage = selectedMerchant.icon;
    }
    if (mothershipQrStyle === "dots") {
      obj.eyeRadius = 20;
    } else if (mothershipQrStyle === "fluid") {
      obj.eyeRadius = 15;
    }
    let hidden = JSON.parse(JSON.stringify(obj));
    obj.size = 150;
    if (obj.logoWidth) {
      if (mothershipFullSizeLogo) {
        obj.logoWidth = 150;
      } else {
        obj.logoWidth = 30;
      }
    }
    if (mothershipQrStyle === "dots") {
      obj.eyeRadius = 13.05;
    } else if (mothershipQrStyle === "fluid") {
      obj.eyeRadius = 9.75;
    }
    hidden.id = "mothership-hidden-qr";

    return { show: obj, hidden: hidden };
  }, [
    selectedMerchant,
    mothershipIncludeLogo,
    mothershipQrStyle,
    mothershipFullSizeLogo,
  ]);

  const qrCodeOptions = useMemo(() => {
    if (!selectedLocation) return null;
    const obj = {
      enableCORS: true,
      size: 230,
      value: `${config.direct_web_prefix}${
        selectedLocation.direct_web_url ?? selectedLocation.store_id
      }`,
      qrStyle: qrStyle,
    };
    if (includeLogo) {
      if (fullSizeLogo) {
        obj.logoWidth = 230;
        obj.logoOpacity = 0.4;
      } else {
        obj.removeQrCodeBehindLogo = true;
        obj.logoWidth = 46;
      }
      obj.logoImage = selectedLocation.icon;
    }
    if (qrStyle === "dots") {
      obj.eyeRadius = 20;
    } else if (qrStyle === "fluid") {
      obj.eyeRadius = 15;
    }
    let hidden = JSON.parse(JSON.stringify(obj));
    obj.size = 150;
    if (obj.logoWidth) {
      if (fullSizeLogo) {
        obj.logoWidth = 150;
      } else {
        obj.logoWidth = 30;
      }
    }
    if (qrStyle === "dots") {
      obj.eyeRadius = 13.05;
    } else if (qrStyle === "fluid") {
      obj.eyeRadius = 9.75;
    }
    hidden.id = "hidden-qr";

    return { show: obj, hidden: hidden };
  }, [selectedLocation, includeLogo, qrStyle, fullSizeLogo]);

  useEffect(() => {
    if (!includeLogo) {
      setFullSizeLogo(false);
    }
  }, [includeLogo]);

  useEffect(() => {
    if (!mothershipIncludeLogo) {
      setMothershipFullSizeLogo(false);
    }
  }, [mothershipIncludeLogo]);

  if (!selectedLocation) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        {/* Title */}
        <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
          Marketing - {selectedMerchant?.name} - {selectedLocation?.name} {" "}
        </h1>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className="grow">
              {/* Panel body */}
              <div className="p-6 space-y-6">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col flex-[2]">
                    <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4 flex gap-2 ">
                      Order Marketing Materials
                    </h2>
                    <div className="text-sm space-y-2">
                      <p>
                        Boost your marketing efforts by incorporating your
                        store’s unique QR code, ready for download now.
                      </p>
                      <p>
                        <span>
                          Add this QR code to key marketing pieces, giving
                          customers a direct link to your online store with a
                          quick scan.{" "}
                        </span>
                        <Tooltip position={"bottom"} className={"inline-block"}>
                          <div className="text-sm w-96">
                            Customize your QR code on the right and download it.
                            When ordering items on our Marketing Store, be sure
                            to upload the QR code to any materials that has a
                            space for an image by clicking the "Upload New
                            Image" button during customization.
                            <div>
                              <img
                                className="w-full"
                                src="https://cdn.saucey.com/direct/dashboard/pro-shop-upload.png"
                                alt="placeholder"
                              />
                            </div>
                          </div>
                        </Tooltip>
                      </p>
                      <p>
                        Don’t forget to check out our extensive suite of
                        marketing assets, designed to elevate your delivery
                        services and enhance customer engagement.
                      </p>
                    </div>
                    <div className="my-4">
                      <PrimaryButton
                        onClick={() =>
                          window.open("https://saucey.ourproshop.com/")
                        }
                      >
                        Visit Marketing Store
                      </PrimaryButton>
                    </div>
                  </div>

                  <div className="flex gap-8">
                    <div className="flex flex-col flex-[1]">
                      {selectedMerchant.locations.length > 1 && (
                        <h2>QR Code for {selectedLocation.name}</h2>
                      )}
                      {config.direct_web_prefix}
                      {selectedLocation.direct_web_url ??
                        selectedLocation.store_id}

                      <div className="mt-2 flex ">
                        <div className="flex flex-col ">
                          <QRCode {...qrCodeOptions.show} />
                          <div className={"hidden"}>
                            <QRCode {...qrCodeOptions.hidden} />
                          </div>
                          <PrimaryButton
                            onClick={() => {
                              const canvas =
                                document.getElementById("hidden-qr");
                              const image = canvas.toDataURL("image/png");
                              const a = document.createElement("a");
                              a.href = image;
                              a.download = "qr-code.png";
                              a.click();
                            }}
                          >
                            Download
                          </PrimaryButton>
                        </div>
                        <div className="ml-4 flex flex-col justify-center">
                          <div>
                            <label htmlFor="qrStyle">QR Style</label>
                            <select
                              id="qrStyle"
                              className="ml-2"
                              onChange={(e) => {
                                setQrStyle(e.target.value);
                              }}
                            >
                              <option value="square">Square</option>
                              <option value="dots">Dots</option>
                              <option value="fluid">Fluid</option>
                            </select>
                          </div>
                          <div className="mt-2">
                            <input
                              type="checkbox"
                              id={`checkbox-includelogo`}
                              onChange={(e) => {
                                setIncludeLogo(e.target.checked);
                              }}
                            />
                            <label
                              htmlFor={`checkbox-includelogo`} // Associates label with the checkbox
                              className="ml-1"
                            >
                              Include Logo
                            </label>
                          </div>
                          {includeLogo && (
                            <div>
                              <input
                                type="checkbox"
                                id={`checkbox-fullsizelogo`}
                                onChange={(e) => {
                                  setFullSizeLogo(e.target.checked);
                                }}
                              />
                              <label
                                htmlFor={`checkbox-fullsizelogo`} // Associates label with the checkbox
                                className="ml-1"
                              >
                                Full Size Logo
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {selectedMerchant.locations.length > 1 && (
                      <div className="flex flex-col flex-[1]">
                        <h2>QR Code for Mothership</h2>
                        {config.direct_web_prefix}
                        {selectedMerchant.direct_web_url ??
                          selectedMerchant.easy_id}
                        <div className="mt-2 flex ">
                          <div className="flex flex-col ">
                            <QRCode {...mothershipQrCodeOptions.show} />
                            <div className={"hidden"}>
                              <QRCode {...mothershipQrCodeOptions.hidden} />
                            </div>
                            <PrimaryButton
                              onClick={() => {
                                const canvas = document.getElementById(
                                  "mothership-hidden-qr"
                                );
                                const image = canvas.toDataURL("image/png");
                                const a = document.createElement("a");
                                a.href = image;
                                a.download = "mothership-qr-code.png";
                                a.click();
                              }}
                            >
                              Download
                            </PrimaryButton>
                          </div>
                          <div className="ml-4 flex flex-col justify-center">
                            <div>
                              <label htmlFor="mothershipQrStyle">
                                QR Style
                              </label>
                              <select
                                id="mothershipQrStyle"
                                className="ml-2"
                                onChange={(e) => {
                                  setMothershipQrStyle(e.target.value);
                                }}
                              >
                                <option value="square">Square</option>
                                <option value="dots">Dots</option>
                                <option value="fluid">Fluid</option>
                              </select>
                            </div>
                            <div className="mt-2">
                              <input
                                type="checkbox"
                                id={`checkbox-mothership-includelogo`}
                                onChange={(e) => {
                                  setMothershipIncludeLogo(e.target.checked);
                                }}
                              />
                              <label
                                htmlFor={`checkbox-mothership-includelogo`} // Associates label with the checkbox
                                className="ml-1"
                              >
                                Include Logo
                              </label>
                            </div>
                            {mothershipIncludeLogo && (
                              <div>
                                <input
                                  type="checkbox"
                                  id={`checkbox-mothership-fullsizelogo`}
                                  onChange={(e) => {
                                    setMothershipFullSizeLogo(e.target.checked);
                                  }}
                                />
                                <label
                                  htmlFor={`checkbox-mothership-fullsizelogo`} // Associates label with the checkbox
                                  className="ml-1"
                                >
                                  Full Size Logo
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Marketing;
